import { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Col, Row, UncontrolledTooltip } from "reactstrap"

import { CanView } from "components/Common/CanView"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { ACCESS } from "constants/accessPermissions"
import {
  useDeleteEmployeeDepartment,
  useEmployeeDepartments,
} from "hooks/useEmployeeDepartment"
import { useListParams } from "hooks/useListParams"
import { formatReadableDate } from "utils/formatDate"

import EmployeeDepartmentAddEdit from "./EmployeeDepartmentAddEdit"

const EmployeeDepartmentList = () => {
  document.title = "Employee Departments"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selected, setSelected] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch } = useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: 10,
      ...(params.search ? { search: params.search } : {}),
    }),
    [params]
  )

  const {
    data: response,
    isLoading,
    isRefetching,
    refetch,
  } = useEmployeeDepartments(queryParams)

  const { mutateAsync: removeDepartment, isPending: deleteLoading } =
    useDeleteEmployeeDepartment()

  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false)
      setSelected(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNew = useCallback(() => {
    setSelected(null)
    setIsEdit(false)
    setModal(true)
  }, [])

  const handleEdit = useCallback(item => {
    setSelected(item)
    setIsEdit(true)
    setModal(true)
  }, [])

  const handleDeleteClick = useCallback(item => {
    setSelected(item)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!selected?.id) return
    try {
      const res = await removeDepartment(selected.id)
      toast.success(res?.message ?? "Department deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (error) {
      console.error(error)
      toast.error(
        error?.response?.data?.message ?? "Failed to delete department."
      )
    }
  }, [selected, removeDepartment, refetch])

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "name",
        cell: ({ row }) => (
          <div className="fw-semibold">{row.original.name || "—"}</div>
        ),
      },
      {
        header: "Slug",
        accessorKey: "slug",
        cell: ({ row }) => (
          <Badge color="light" className="text-dark">
            {row.original.slug || "—"}
          </Badge>
        ),
      },
      {
        header: "Description",
        cell: ({ row }) => (
          <div style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
            {row.original.description || "—"}
          </div>
        ),
      },
      {
        header: "Created",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "—",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const item = row.original
          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              <CanView access={[ACCESS.EMPLOYEE_DEPARTMENT.EDIT]}>
                <li>
                  <Button
                    color="info"
                    size="sm"
                    onClick={() => handleEdit(item)}
                    id={`edit-dept-${item.id}`}
                  >
                    <i className="mdi mdi-pencil-outline" />
                  </Button>
                  <UncontrolledTooltip target={`edit-dept-${item.id}`}>
                    Edit
                  </UncontrolledTooltip>
                </li>
              </CanView>
              <CanView access={[ACCESS.EMPLOYEE_DEPARTMENT.DELETE]}>
                <li>
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => handleDeleteClick(item)}
                    id={`del-dept-${item.id}`}
                  >
                    <i className="mdi mdi-delete-outline" />
                  </Button>
                  <UncontrolledTooltip target={`del-dept-${item.id}`}>
                    Delete
                  </UncontrolledTooltip>
                </li>
              </CanView>
            </ul>
          )
        },
      },
    ],
    [handleEdit, handleDeleteClick]
  )

  const items = response?.data?.data ?? []
  const paginationInfo = response?.data ?? null

  return (
    <PageWrapper
      title="Employee Departments"
      breadcrumbItem="Employee Departments"
    >
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="mb-3 align-items-center">
        <Col sm={6}>
          <h5 className="mb-0">Employee Departments</h5>
        </Col>
        <Col sm={6} className="text-end">
          <CanView access={[ACCESS.EMPLOYEE_DEPARTMENT.ADD]}>
            <Button color="primary" onClick={handleAddNew} className="me-1">
              <i className="bx bx-plus" /> Add Department
            </Button>
          </CanView>
          <Button color="light" onClick={() => refetch()} id="refresh-dept">
            <i
              className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`}
            />
          </Button>
          <UncontrolledTooltip target="refresh-dept">
            Refresh
          </UncontrolledTooltip>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={items}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        tableClass="align-middle table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline mt-2 border-top"
      />

      <EmployeeDepartmentAddEdit
        modal={modal}
        toggleModal={toggleModal}
        isEdit={isEdit}
        editedData={selected}
      />
    </PageWrapper>
  )
}

export default EmployeeDepartmentList
